import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  Col,
  Row,
  UiBox,
  UiButton,
  UiImage,
  UiInput,
  UiQuestionnaireLayout,
  UiSimpleGrid,
  UiText,
} from '~/components/elements';
import {UiRangePicker} from '~/components/elements/UiRangePicker';
import {ScrollToBottomButton} from '~/components/shared/ScrollToBottomButton';
import {EMERGENCY_CONTRACEPTION_KEYS} from '~/constants/medical-questionnaires';
import {useInput, useNotification} from '~/hooks';
import {setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {spacing} from '~/utils/theme/spacing';

const Questionnaire4Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const {addMessage} = useNotification(dispatch);

  const currentAnswer = useSelector(
    (state: RootState) =>
      state.questionnaires?.collectData[EMERGENCY_CONTRACEPTION_KEYS.EMERGENCY_CONTRACEPTION_USED]?.data,
  );

  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);

  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === EMERGENCY_CONTRACEPTION_KEYS.EMERGENCY_CONTRACEPTION_USED);

  const {inputs, setInputs, trimSpacesOnChange, trimSpaces} = useInput({
    daysUsed: currentAnswer?.daysUsed !== undefined ? currentAnswer?.daysUsed : undefined,
    brandUsed: currentAnswer?.brandUsed || '',
    selectedOption: currentAnswer?.selectedOption || '',
  });

  const onClickSubmit = async () => {
    dispatch(
      setQuestionnaireAnswer({
        key: EMERGENCY_CONTRACEPTION_KEYS.EMERGENCY_CONTRACEPTION_USED,
        index: questionnaire?.index || '',
        data: {...inputs, brandUsed: trimSpaces(inputs.brandUsed)},
      }),
    );
    dispatch(setNextQuestionnaire({value: EMERGENCY_CONTRACEPTION_KEYS.LAST_UNPROTECTED_INTERCOURSE}));
  };

  const renderQuestion = () => (
    <UiBox className="animate__animated animate__fadeInLeft">
      <UiText fontSize="md" fontWeight="700" mt={spacing.large}>
        When did you last use emergency contraception?
      </UiText>
      <Row alignItems="end" justifyContent="center" mt={spacing.medium}>
        <UiRangePicker
          range={[0, 30]}
          unit="days ago"
          selectedValue={inputs.daysUsed}
          onChange={value => {
            setInputs({daysUsed: value});
          }}
        />
      </Row>
      <UiText fontSize="md" fontWeight="700" mt={spacing.large}>
        Which brand did you use?
      </UiText>
      <UiInput
        mt={spacing.medium}
        variant="flushed"
        borderColor="baseColor"
        width={spacing.huge}
        fontSize="md"
        fontWeight="600"
        opacity={0.4}
        value={inputs.brandUsed}
        textAlign="center"
        onChange={({target}) => setInputs({brandUsed: trimSpacesOnChange(target.value)})}
      />
    </UiBox>
  );

  return (
    <>
      <UiQuestionnaireLayout
        backIcon
        disableNext={
          !inputs.selectedOption ||
          (inputs.selectedOption === 'YES' && (!trimSpaces(inputs.brandUsed) || inputs.daysUsed === undefined))
        }
        handleOnNext={onClickSubmit}
        actions={questionnaire?.actions}
        checkoutStep={questionnaire?.checkoutStep}
        minH="calc(100vh + 50px)"
      >
        <Col alignItems="center" textAlign="center">
          <UiImage type="icon" name={questionnaire?.icon} boxSize={spacing.mLarge} mt={spacing.medium} />
          <UiText fontSize="lg" fontWeight="bold" mt={spacing.large}>
            {questionnaire?.questions?.default}
          </UiText>
          <UiBox mt={spacing.small}>
            <UiSimpleGrid columns={2} spacing={5}>
              {questionnaire?.options?.map(value => (
                <ScrollToBottomButton
                  key={`${value.label}-${value.value}`}
                  title={value.label}
                  variant={inputs.selectedOption === value.value ? 'solid' : 'toggle'}
                  onClick={() => setInputs({selectedOption: value.value})}
                />
              ))}
            </UiSimpleGrid>
          </UiBox>
          {inputs.selectedOption === 'YES' && renderQuestion()}
        </Col>
      </UiQuestionnaireLayout>
    </>
  );
};

export default Questionnaire4Screen;
